import { useEffect, useState } from "react";
import {
  ManageProjectParticipantResourceAccessDocument,
  ProjectFragment,
  PublicUserOrganization,
} from "../../../../../../__generated__/graphql";
import Modal from "../../../../../../components/Modal";
import UserOrganizationItem from "../../../../../../components/UserOrganizationItem";
import { formatDateWithDay } from "../../../../../../lib/utils";
import Checkbox from "../../../../../../components/Checkbox";
import Button from "../../../../../../components/Button";
import { LockOpenIcon } from "@heroicons/react/24/solid";
import { useMutation } from "@apollo/client";
import { toast } from "react-hot-toast";

interface ResourcesManagementModalProps {
  project: ProjectFragment;
  participants: PublicUserOrganization[];
  isOpen: boolean;
  onClose: () => void;
}

type PublicUserOrganizationEventsMap = {
  [userOrganizationId: string]: {
    eventIds: string[];
  };
};

export default function ResourcesManagementModal({
  project,
  participants,
  isOpen,
  onClose,
}: ResourcesManagementModalProps) {
  const [selectedParticipant, setSelectedParticipant] =
    useState<PublicUserOrganization | null>(null);
  const [resourcesSelection, setResourcesSelection] =
    useState<PublicUserOrganizationEventsMap>({});

  // Display participants selections in a horizontal scrollable list (like tabs)
  // On select a participant, we display a list of resources for this participant
  // The resources are:
  // - the events of the project that the viewer can see (should be ok)
  // - the non public documents of the project that the viewer has created

  const [manageProjectParticipantResourceAccess, { loading }] = useMutation(
    ManageProjectParticipantResourceAccessDocument
  );

  useEffect(() => {
    if (participants.length > 0) {
      setSelectedParticipant(participants[0]);
    }
  }, [participants]);

  useEffect(() => {
    // If no ressources to share, we close the modal
    // TODO: add documents
    if (project.events?.length === 0) {
      onClose();
    }
  }, [project.documents, project.events, onClose]);

  const participantsSelection = () => {
    return (
      <div className="flex gap-2 overflow-x-auto">
        {participants.map((participant) => (
          <div key={participant.id} className="flex-shrink-0">
            <UserOrganizationItem
              userOrganization={participant}
              selectionMode="radio"
              selected={selectedParticipant?.id === participant.id}
              onClick={() => setSelectedParticipant(participant)}
              className={
                selectedParticipant?.id === participant.id
                  ? "border border-accent"
                  : ""
              }
            />
          </div>
        ))}
      </div>
    );
  };

  const eventsList = () => {
    if (!project.events) return null;

    return (
      <div className="flex flex-col gap-2 items-start">
        <div className="flex items-center justify-between w-full gap-2">
          <div className="text-sm text-primary font-bold">Évènements:</div>
          <Button
            variant="secondary"
            className="p-0 gap-2"
            onClick={() => {
              const events = project.events ?? [];
              if (events) {
                setResourcesSelection((prev) => ({
                  ...prev,
                  [selectedParticipant?.id ?? ""]: {
                    eventIds: events.map((e) => e?.id ?? ""),
                  },
                }));
              }
            }}
          >
            <LockOpenIcon className="w-4 h-4" />
            Tout sélectionner
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {project.events.map((event) => {
            if (!event) return null;

            const date = formatDateWithDay(event.startDate);
            const location = event.eventLocation?.location?.label;
            const name = event.name;

            const isSelected = resourcesSelection[
              selectedParticipant?.id ?? ""
            ]?.eventIds.includes(event.id ?? "");

            return (
              <div
                key={event.id}
                className="flex items-center gap-2 flex-shrink-0 bg-card rounded-md p-2 border border-border cursor-pointer hover:bg-card/50"
                onClick={() => {
                  if (isSelected) {
                    setResourcesSelection((prev) => ({
                      ...prev,
                      [selectedParticipant?.id ?? ""]: {
                        eventIds: prev[
                          selectedParticipant?.id ?? ""
                        ]?.eventIds.filter((e) => e !== event.id),
                      },
                    }));
                  } else {
                    setResourcesSelection((prev) => ({
                      ...prev,
                      [selectedParticipant?.id ?? ""]: {
                        eventIds: [
                          ...(prev[selectedParticipant?.id ?? ""]?.eventIds ??
                            []),
                          event.id ?? "",
                        ],
                      },
                    }));
                  }
                }}
              >
                <Checkbox checked={isSelected} />
                <div>
                  <div className="text-xs text-secondary">{date}</div>
                  <div className="font-bold">{name}</div>
                  {location ? (
                    <div className="text-xs text-accent">{location}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleSave = async () => {
    try {
      await Promise.all(
        Object.entries(resourcesSelection).map(
          async ([userOrganizationId, resources]) => {
            await manageProjectParticipantResourceAccess({
              variables: {
                manageProjectParticipantResourceAccessInput: {
                  projectId: project.id,
                  participantUserOrganizationId: userOrganizationId,
                  eventIds: resources.eventIds,
                },
              },
            });
          }
        )
      );

      toast.success("Les accès ont été enregistrés avec succès");

      setResourcesSelection({});
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de l'enregistrement"
      );
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={onClose}
      title="Gérer les accès"
      description="Chaque participant peut avoir des accès différents aux ressources du projet. Sélectionnez pour chaque participant les évènements auxquels il a accès."
      className="max-w-2xl"
    >
      <div>{participantsSelection()}</div>
      <div>{eventsList()}</div>
      <Button onClick={handleSave} isLoading={loading}>
        Enregistrer
      </Button>
    </Modal>
  );
}
